import { splitEntry } from "../../utils-Bd28bWgm.js";
import { useArgoDesign } from "../../context-U293r3v-.js";
import { useControllableState } from "../../useControllableState-CUYXMsfz.js";
import { forwardAccessibleName } from "../utils/accessibleName.js";
import { Combobox as Combobox$1, ComboboxClearButton, ComboboxOpenButton, ComboboxOptions, createCreatableItem, defaultGroupToLabel, defaultItemToDisabled, defaultItemToIcon, defaultQueryToCreateLabel, defaultRowLabel, defaultToCreateValue, flatItems, isCreatableItem, isGroupHeaderItem, isSelectAllItem, sanitizeTypedInput, selectAllItem, toEntries, toLabel, useCreatableStringValue, useGroupHeaders, useItemEquality, useQueryReporter, useVirtualList, useWithCreated } from "../../Combobox-BNhW0TDB.js";
import { useCallback, useMemo, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Combobox } from "@base-ui/react/combobox";
//#region src/blocks/MultiCombobox/MultiCombobox.tsx
/** Joins the create row's values with the entry's separator; a `RegExp` uses the default one. */
const joinCreateParts = (parts, separator) => parts.join(`${typeof separator === "string" ? separator : ","} `);
const NO_ITEMS = [];
const NO_VALUES = [];
/**
* Searchable multi-select with chips, built on Base UI Combobox.
*
* @see RECIPES.md#creatable-multi-combobox
* @see RECIPES.md#filter-toolbar
*/
const MultiCombobox = ({ items: _itemsProp, value: _valueProp, defaultValue, onValueChange, itemToStringLabel, itemToStringValue, disabled, filter: filterProp, isItemEqualToValue: isItemEqualToValueProp, inputRef, variant, sizing, focusVariant, state, placeholder, allowClear = true, name, onBlur, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid, "aria-required": ariaRequired, className, allowSelectAll = false, empty = "No items", isLoading = false, indicator, virtualized = false, inputValueSanitizer, onQueryChange, allowCreate, canCreate, queryToCreateValue, queryToCreateLabel = defaultQueryToCreateLabel, createSeparator = ",", itemToDisabled = defaultItemToDisabled, itemToLabel: itemToLabelProp, itemToIcon = defaultItemToIcon, groupToLabel = defaultGroupToLabel, collapsibleGroups = false, selectableGroups = false, itemToChipLabel, itemToChipColor, pinnedChipLabels, renderChips, maxVisible = 3, ...accessibleName }) => {
	const itemsProp = _itemsProp ?? NO_ITEMS;
	const [valueProp, setValueProp] = useControllableState({
		prop: _valueProp === null ? NO_VALUES : _valueProp,
		defaultProp: defaultValue ?? NO_VALUES
	});
	const itemToString = useCallback((item) => itemToStringLabel?.(item) ?? toLabel(item), [itemToStringLabel]);
	const itemToLabel = itemToLabelProp ?? defaultRowLabel(itemToStringLabel);
	const chipVariant = useArgoDesign() ? "pill" : "default";
	const [inputValue, setInputValue] = useState("");
	const [createdItems, setCreatedItems] = useState([]);
	const query = inputValue.trim();
	useQueryReporter(query, onQueryChange);
	const busy = isLoading || void 0;
	const ownsInput = Boolean(allowCreate || inputValueSanitizer || onQueryChange || itemToLabelProp || collapsibleGroups || selectableGroups);
	const baseItems = useWithCreated(itemsProp, createdItems);
	const allFlatItems = flatItems(baseItems);
	const sampleItem = allFlatItems[0];
	const hasSelectAllRow = allowSelectAll && allFlatItems.length > 0;
	const isAllSelected = hasSelectAllRow && allFlatItems.length === (valueProp?.length ?? 0);
	const defaultFilter = Combobox.useFilter({
		multiple: true,
		value: valueProp
	});
	const wrappedItemToStringLabel = useMemo(() => {
		if (!allowCreate && !collapsibleGroups && !selectableGroups) return itemToStringLabel && itemToString;
		return (item) => {
			if (isCreatableItem(item)) return item.value;
			if (isGroupHeaderItem(item)) return item.label;
			return itemToString(item);
		};
	}, [
		allowCreate,
		collapsibleGroups,
		selectableGroups,
		itemToStringLabel,
		itemToString
	]);
	const itemFilter = filterProp ?? defaultFilter.contains;
	const isItemEqualToValue = useItemEquality(isItemEqualToValueProp);
	const groupHeaders = useGroupHeaders({
		items: itemsProp,
		collapsible: collapsibleGroups,
		selectable: selectableGroups,
		query,
		matches: (item) => itemFilter(item, query, wrappedItemToStringLabel),
		isItemEqualToValue
	});
	const listItems = useWithCreated(groupHeaders.items, createdItems);
	const virtualList = useVirtualList(baseItems, virtualized);
	const labelKey = (item) => itemToString(item).toLowerCase();
	const createParts = !allowCreate || disabled || !query ? [] : splitEntry(query, createSeparator);
	const selectedKeys = /* @__PURE__ */ new Set();
	if (createParts.length > 0) for (const item of valueProp) selectedKeys.add(labelKey(item));
	const itemKeys = /* @__PURE__ */ new Set();
	if (createParts.length > 0 && canCreate) for (const item of allFlatItems) itemKeys.add(labelKey(item));
	const namesAnItem = (key) => canCreate ? itemKeys.has(key) : allFlatItems.some((item) => labelKey(item) === key);
	/**
	* The entry's values not yet selected, minus those `canCreate` rejects.
	*
	* - Both names the create row and is exactly what committing it adds.
	* - A value naming an existing item counts and bypasses `canCreate` — committing selects it.
	*/
	const addedCreateParts = createParts.filter((part) => {
		const key = part.toLowerCase();
		if (selectedKeys.has(key)) return false;
		return !canCreate || namesAnItem(key) || canCreate(part);
	});
	const soleValueKey = createParts.length === 1 ? createParts[0].toLowerCase() : void 0;
	const showCreateRow = addedCreateParts.length > 0 && (soleValueKey === void 0 || !namesAnItem(soleValueKey));
	const items = useMemo(() => {
		const withSelectAll = hasSelectAllRow ? [...toEntries(listItems, [selectAllItem]), ...listItems] : listItems;
		return showCreateRow ? [...withSelectAll, ...toEntries(listItems, [createCreatableItem(query)])] : withSelectAll;
	}, [
		hasSelectAllRow,
		showCreateRow,
		listItems,
		query
	]);
	const filterHeader = groupHeaders.enabled ? groupHeaders.filterHeader : void 0;
	const filter = useCallback((item, query, itemToString) => {
		if (isSelectAllItem(item)) return query.trim() === "";
		if (isCreatableItem(item)) return true;
		return filterHeader?.(item) ?? itemFilter(item, query, itemToString);
	}, [filterHeader, itemFilter]);
	const wrappedItemToStringValue = useCreatableStringValue(itemToStringValue, allowCreate);
	const value = isAllSelected ? [...valueProp, selectAllItem] : valueProp;
	const selectedSet = selectableGroups && !isItemEqualToValueProp ? new Set(valueProp) : void 0;
	const isSelected = (item) => selectedSet?.has(item) ?? valueProp.some((selected) => isItemEqualToValue(item, selected));
	/** What a group's row selects: the group's items the query matches, the disabled aside. */
	const groupTargets = (row) => groupHeaders.headerOf(row)?.matching.filter((item) => !itemToDisabled(item)) ?? [];
	/**
	* Replaces a pressed group row in `next` with its items.
	*
	* - Base UI adds the row to `value` like any unselected item.
	* - A row whose items are all selected deselects them.
	*/
	const selectGroupRow = (next) => {
		const row = next.find(isGroupHeaderItem);
		if (row === void 0) return next;
		const rest = next.filter((item) => item !== row);
		const targets = groupTargets(row);
		if (!targets.every(isSelected)) return [...rest, ...targets.filter((item) => !isSelected(item))];
		return rest.filter((item) => !targets.some((target) => isItemEqualToValue(target, item)));
	};
	const groupRowChecked = (targets) => {
		const selectedCount = targets.filter(isSelected).length;
		if (selectedCount === 0) return false;
		return selectedCount === targets.length ? true : "mixed";
	};
	const handleInputValueChange = (next, { reason }) => {
		setInputValue(sanitizeTypedInput(next, reason, inputValueSanitizer));
	};
	const handleValueChange = (next, event) => {
		if (ownsInput) setInputValue((prev) => prev === "" ? prev : "");
		if (!onValueChange && _valueProp !== void 0) return;
		const picked = selectGroupRow(next);
		const created = [];
		const converted = (() => {
			if (!allowCreate || !picked.some(isCreatableItem)) return picked;
			const itemsByLabel = /* @__PURE__ */ new Map();
			for (const item of allFlatItems) itemsByLabel.set(labelKey(item), item);
			return picked.flatMap((item) => {
				if (!isCreatableItem(item)) return [item];
				return addedCreateParts.map((part) => {
					const existing = itemsByLabel.get(part.toLowerCase());
					if (existing !== void 0) return existing;
					const value = defaultToCreateValue(part, sampleItem, queryToCreateValue);
					created.push(value);
					return value;
				});
			});
		})();
		const hasSelectAll = converted.some((item) => isSelectAllItem(item));
		const newValue = (() => {
			if (!hasSelectAllRow) return converted;
			if (!hasSelectAll) return isAllSelected ? [] : converted;
			return isAllSelected ? converted.filter((item) => !isSelectAllItem(item)) : allFlatItems;
		})();
		if (allowCreate) {
			const selected = new Set(newValue.map(itemToString));
			setCreatedItems((prev) => [...prev, ...created].filter((item) => selected.has(itemToString(item))));
		}
		setValueProp(newValue);
		onValueChange?.(newValue, event);
	};
	return /* @__PURE__ */ jsxs(Combobox$1.Root, {
		multiple: true,
		autoHighlight: true,
		items,
		value,
		filter,
		isItemEqualToValue,
		onValueChange: handleValueChange,
		onOpenChange: groupHeaders.onOpenChange,
		onItemHighlighted: (item, details) => {
			groupHeaders.onItemHighlighted(item);
			virtualList.onItemHighlighted(details);
		},
		virtualized: virtualList.enabled,
		itemToStringLabel: wrappedItemToStringLabel,
		itemToStringValue: wrappedItemToStringValue,
		disabled,
		inputRef,
		...ownsInput ? {
			inputValue,
			onInputValueChange: handleInputValueChange
		} : {},
		children: [/* @__PURE__ */ jsx(Combobox$1.InputGroup, {
			variant,
			sizing,
			state,
			focusVariant,
			className,
			children: /* @__PURE__ */ jsxs(Combobox.Chips, {
				className: "tw:flex tw:min-w-0 tw:flex-1 tw:flex-wrap tw:items-center tw:gap-1.5 tw:px-4 tw:py-1.5",
				children: [/* @__PURE__ */ jsx(Combobox$1.Chips, {
					maxVisible,
					variant: chipVariant,
					itemToChipLabel: itemToChipLabel ?? ((item) => itemToLabel(item, "")),
					itemToChipColor,
					pinnedChipLabels,
					children: renderChips
				}), /* @__PURE__ */ jsxs("div", {
					className: "tw:flex tw:min-w-0 tw:grow tw:items-center tw:gap-1",
					children: [
						/* @__PURE__ */ jsx(Combobox$1.Input, {
							...forwardAccessibleName(accessibleName),
							name,
							onBlur,
							"aria-describedby": ariaDescribedBy,
							"aria-invalid": ariaInvalid,
							"aria-required": ariaRequired,
							"aria-busy": busy,
							placeholder,
							disabled,
							onKeyDown: groupHeaders.onInputKeyDown,
							className: "tw:w-auto tw:min-w-0 tw:grow tw:basis-[120px] tw:!px-0 tw:!py-0"
						}),
						allowClear && /* @__PURE__ */ jsx(ComboboxClearButton, { disabled }),
						/* @__PURE__ */ jsx(ComboboxOpenButton, {
							disabled,
							isLoading
						})
					]
				})]
			})
		}), /* @__PURE__ */ jsx(Combobox$1.Content, { children: /* @__PURE__ */ jsx(ComboboxOptions, {
			isLoading,
			empty,
			virtualized: virtualList.enabled,
			listRef: virtualList.ref,
			groupHeaders,
			groupToLabel,
			indicator,
			itemToLabel: (item) => itemToLabel(item, query),
			itemToIcon,
			itemToDisabled,
			createLabel: () => queryToCreateLabel(joinCreateParts(addedCreateParts, createSeparator)),
			groupRowState: selectableGroups ? (row) => {
				const targets = groupTargets(row);
				return {
					checked: groupRowChecked(targets),
					disabled: targets.length === 0
				};
			} : void 0
		}) })]
	});
};
//#endregion
export { MultiCombobox };

//# sourceMappingURL=MultiCombobox.js.map